import type { EmailLayoutType } from "@crm-src/lib/email-layouts"
import type { TargetSelection } from "@crm-src/components/email-target-selector"

/**
 * The fields a save path wants to persist on a campaign draft. `layout` is
 * intentionally `| undefined` (not optional) so callers can always pass
 * `campaign.layout` under `exactOptionalPropertyTypes`.
 */
export interface DraftFields {
  subject: string
  content: string
  layout: EmailLayoutType | undefined
  target_selection: TargetSelection
  content_data: unknown
  /** Per-campaign sender overrides. Blank/undefined ⇒ crm-api defaults. */
  from_address?: string
  from_name?: string
  reply_to?: string
}

export interface CampaignSaverDeps {
  /**
   * Returns the id of an existing row to UPDATE, or null to CREATE. Read fresh
   * on every call (NOT captured once) so edit-mode rows — and a draft this
   * saver just created — update in place instead of spawning a new one.
   */
  getExistingId: () => string | null
  createCampaign: (data: Record<string, unknown>) => Promise<{ campaign: { id: string } }>
  updateCampaign: (id: string, data: Record<string, unknown>) => Promise<unknown>
  /** Called once, synchronously after a create resolves, with the new id. */
  onCreated: (id: string) => void
}

/**
 * Race-safe "ensure this campaign exists, then persist these fields" — the
 * single owner of draft creation, shared by the campaign form's three save
 * paths (debounced auto-save, manual Save, back-navigation).
 *
 * The bug this fixes ("creating one campaign makes two"): the old form had two
 * independent create paths gated only by React *state* (`draftCampaignId`),
 * which lags a render. On a slow network the auto-save fired before the
 * template-select create's id had landed in state, so it created a SECOND row.
 *
 * Here, concurrent callers share ONE in-flight create promise: the first writer
 * creates, every later caller awaits that same id and UPDATEs instead. There is
 * no window in which a second create can start.
 */
export function makeCampaignSaver(deps: CampaignSaverDeps) {
  let creating: Promise<string> | null = null

  const payloadFor = (f: DraftFields): Record<string, unknown> => ({
    subject: f.subject || "Untitled Campaign",
    content: f.content,
    layout: f.layout,
    target_selection: f.target_selection,
    content_data: f.content_data,
    status: "not_sent",
    // Always send the sender fields (even empty) so clearing one resets it to
    // the server default — crm-api normalizes "" → nil. Omitting the key on a
    // PUT would instead keep the previous value.
    from_address: f.from_address ?? "",
    from_name: f.from_name ?? "",
    reply_to: f.reply_to ?? "",
  })

  async function ensure(fields: DraftFields): Promise<string> {
    const payload = payloadFor(fields)

    // Already have a row (edit mode, or a draft we created earlier): update it.
    const existingId = deps.getExistingId()
    if (existingId) {
      await deps.updateCampaign(existingId, payload)
      return existingId
    }

    // A create is already in flight from another path: await its id, then apply
    // our update on top. No second row is created.
    if (creating) {
      const id = await creating
      await deps.updateCampaign(id, payload)
      return id
    }

    // First writer wins: create exactly once, publish the id via onCreated
    // before the promise settles for any waiter.
    const createPromise = (async () => {
      const result = await deps.createCampaign({
        name: fields.subject?.trim() || "Untitled Campaign",
        ...payload,
      })
      const id = result.campaign.id
      deps.onCreated(id)
      return id
    })()
    creating = createPromise
    try {
      return await createPromise
    } finally {
      creating = null
    }
  }

  return { ensure }
}

export type CampaignSaver = ReturnType<typeof makeCampaignSaver>
